import { context, trace } from '@opentelemetry/api';
import type React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { APP_DOMAIN, getCurrentAppId } from '../config/jitsiConfig';
import { authRedirectService } from '../services/authRedirectService';
import { isHostedByTeamHub } from '../services/hostAuthService';
import { type MeetingExitAction, meetingSessionService } from '../services/meetingSessionService';
import { closeMeetingPopup } from '../services/meetingWindow/popupMode';
import { settingsService } from '../services/settingsService';
import { applyClientMetadata, refreshCallStatsUserName } from '../telemetry/clientMetadata';
import { getUiLogger, getUiTracer } from '../telemetry/otelClient';
import { decodeJWT } from './decodeJWT';
import {
  applyPersistedDevicesToJitsiApi,
  type DeviceApplyKind,
  getPersistedDeviceSelections,
  prepareDevicesForMeetingJoin,
  setJitsiSessionActive,
} from './deviceUtils';
import { devDebug, devLog } from './devLog';
import { buildJitsiConfigOverwrite } from './jitsiConfigUtils';
import {
  exitMeetingFullscreen,
  FULLSCREEN_BUTTON_ID,
  toggleMeetingFullscreen,
} from './meetingFullscreen';
import { isDev } from './optimizedImports';

export const JAAS_RELEASE: string = 'release-6869';

declare global {
  interface Window {
    // biome-ignore lint/suspicious/noExplicitAny: JitsiMeetExternalAPI has no TypeScript definitions
    JitsiMeetExternalAPI: any;
  }
}

/**
 * Waits for JitsiMeetExternalAPI to be available on the window object
 * @param maxWaitTime Maximum time to wait in milliseconds (default: 10000ms)
 * @returns Promise that resolves when JitsiMeetExternalAPI is available
 */
export const waitForJitsiAPI = (
  maxWaitTime: number = 7500,
): Promise<typeof window.JitsiMeetExternalAPI> => {
  return new Promise((resolve, reject) => {
    // Check if already available
    if (typeof window !== 'undefined' && window.JitsiMeetExternalAPI) {
      resolve(window.JitsiMeetExternalAPI);
      return;
    }

    const startTime = Date.now();
    const checkInterval = 150; // Check every 150ms

    const checkForApi = () => {
      if (typeof window !== 'undefined' && window.JitsiMeetExternalAPI) {
        resolve(window.JitsiMeetExternalAPI);
        return;
      }

      const elapsed = Date.now() - startTime;
      if (elapsed >= maxWaitTime) {
        reject(
          new Error(
            'JitsiMeetExternalAPI failed to load within the expected time. Please ensure the application is loaded correctly.',
          ),
        );
        return;
      }

      setTimeout(checkForApi, checkInterval);
    };

    checkForApi();
  });
};

export interface JitsiErrorPayload {
  details?: Record<string, unknown>;
  message?: string;
  name: string;
  type: string;
  isFatal: boolean;
}

const loadJitsiMeetId = (): string | null => {
  try {
    const strdata = localStorage.getItem('jitsiLocalStorage');
    if (strdata) {
      const valdata = JSON.parse(strdata);
      if (valdata) {
        devDebug('[jitsiLocalStorage] parsed:', valdata);

        const id = valdata?.jitsiMeetId;

        if (typeof id === 'string') {
          devDebug('[jitsiLocalStorage] preloaded deviceId:', id);
          return id;
        }
      } else {
        devDebug('[jitsiLocalStorage] empty json data');
      }
    } else {
      devDebug('[jitsiLocalStorage] empty string data');
    }
  } catch (e: unknown) {
    console.warn(
      `[jitsiLocalStorage] not parseable (${e instanceof Error ? e.message : String(e)})`,
    );
  }

  return null;
};

const generateJitsiMeetId = async (): Promise<string> => {
  const id = loadJitsiMeetId();

  if (id) {
    return id;
  }

  // In TeamHub-hosted (Module Federation) mode the host page never loads
  // Jitsi's `external_api.js` — meetings open in a popup on NG Meet's own
  // origin instead (see `services/meetingWindow`). Spinning up the hidden
  // pre-init iframe here can therefore never succeed; `waitForJitsiAPI()`
  // would just block for its full timeout and then reject. Bail out fast so
  // callers fall back to a synthetic device id instead of blocking/logging.
  if (isHostedByTeamHub()) {
    devDebug('[jitsiMeetId] hosted (MF) mode — skipping in-page Jitsi pre-init');
    throw new Error(
      'JitsiMeetExternalAPI is unavailable in hosted (MF) mode — external_api.js is not loaded on the host page.',
    );
  }

  devDebug('[jitsiMeetId] no cached ID found, running initialization...');

  const JitsiMeetExternalApi = await waitForJitsiAPI();

  const parentNode = document.createElement('div');
  parentNode.style.position = 'absolute';
  parentNode.style.opacity = '0';
  document.body.appendChild(parentNode);

  // create temporary random room ID
  const roomNameUuid = uuidv4().replace(/-/g, '');
  const appId = getCurrentAppId();
  const roomName = `${appId}/${roomNameUuid}`;

  // initialize Jitsi API for temporary room
  const api = new JitsiMeetExternalApi(APP_DOMAIN, {
    // NOTE: jwt is NOT passed here so a conference join failure is generated
    // NOTE: it still causes the presence code to initialize the jitsiMeetId,
    // NOTE: which can then be retrieved by the code below
    parentNode,
    roomName,
    release: JAAS_RELEASE,
    configOverwrite: {
      useHostPageLocalStorage: true,
      // do not request user media
      disableInitialGUM: true,
      // disable and mute devices
      startWithAudioMuted: true,
      startWithVideoMuted: true,
      devices: {
        audioInput: 'none',
        audioOutput: 'none',
        videoInput: 'none',
      },
      // join without waiting
      prejoinConfig: {
        enabled: false,
      },
      deeplinking: {
        desktop: {
          enabled: false,
        },
        disabled: true,
      },
    },
    onload: () => {
      devDebug('[jitsiMeetId] pre-init API loaded');
    },
  });

  const startTime = Date.now();
  const maxWaitTime: number = 15000;
  const checkInterval = 500; // Check every 500ms

  devDebug('[jitsiMeetId] waiting for device ID to be initialized...');

  const idPromise = new Promise<string>((resolve, reject) => {
    const checkForId = () => {
      const id = loadJitsiMeetId();

      if (id) {
        devLog('[jitsiMeetId] new device ID:', id);
        resolve(id);
        return;
      }

      const elapsed = Date.now() - startTime;

      if (elapsed >= maxWaitTime) {
        console.error('[jitsiMeetId] timeout waiting for device ID');
        reject(
          new Error(
            'Failed to generate new device ID within the expected time. Please ensure the application is loaded correctly.',
          ),
        );
        return;
      }

      setTimeout(checkForId, checkInterval);
    };

    checkForId();
  });

  try {
    return await idPromise;
  } finally {
    api.dispose();
    parentNode.remove();
  }
};

// Lazily-created, memoized device-id promise. Deliberately NOT started at
// module import time: an eager IIFE would begin `waitForJitsiAPI()` before any
// caller attached a handler, and in MF/hosted mode (no `external_api.js` on the
// host page) it would reject during that window and surface as an
// "Uncaught (in promise)". Creating it on first `retrieveJitsiMeetId()` call
// guarantees the caller's `await`/`.catch()` is attached synchronously.
// It stays a one-shot singleton (a rejected result is memoized too) so repeated
// callers share the same initialization and never re-run it — see
// MeetingOverlays' "reload, not refetch" note.
let _jitsiMeetIdPromise: Promise<string> | undefined;

export const retrieveJitsiMeetId = async (): Promise<string> => {
  if (!_jitsiMeetIdPromise) {
    _jitsiMeetIdPromise = generateJitsiMeetId();
  }
  return await _jitsiMeetIdPromise;
};

export interface InitializeJitsiAPICallbacks {
  setIsMeetingVisible: (visible: boolean) => void;
  setIsChatOpen: (open: boolean) => void;
  removeOverlay: (id: string) => void;
  startDeviceSync: (
    // biome-ignore lint/suspicious/noExplicitAny: Jitsi external API has no TypeScript definitions
    api: any,
    gate?: {
      getAppliedSlots: () => ReadonlySet<DeviceApplyKind>;
      getSavedDevices: () => {
        selectedCamera: string;
        selectedMicrophone: string;
        selectedAudioOutput: string;
      };
    },
  ) => void;
  cleanup: () => void;
  // biome-ignore lint/suspicious/noExplicitAny: Jitsi external API has no TypeScript definitions
  setupPasswordListener: (api: any) => void;
  onJitsiError: (payload: JitsiErrorPayload) => void;
  clearJitsiErrors: () => void;
  onConferenceJoined?: () => void;
  onLobbyEntered?: () => void;
  /** SM-1165: fired as soon as hang-up teardown starts, before the meeting is closed/navigated away from. */
  onMeetingLeaving?: () => void;
  navigate: (path: string, options?: { replace?: boolean }) => void;
  removeSessionForMeetingLink: () => void;
  isMeetingSetUp: React.MutableRefObject<boolean>;
  // biome-ignore lint/suspicious/noExplicitAny: Jitsi external API has no TypeScript definitions
  apiRef: React.MutableRefObject<any>;
  cancelPendingRef: React.MutableRefObject<(() => void) | null>;
}

interface InitializeJitsiAPIParams {
  meetingId: string;
  meetingDisplayName: string | null;
  activeToken: string;
  isAuthenticated: boolean;
  extraParams?: Record<string, string>;
  callbacks: InitializeJitsiAPICallbacks;
  /** Seed Jitsi's display name at iframe creation (SSO name or guest name). */
  displayName?: string | null;
}

interface JitsiDisposableApi {
  dispose: () => void;
}

/**
 * Creates and configures a Jitsi API instance with all event listeners
 */
export const initializeJitsiAPI = async ({
  meetingId,
  meetingDisplayName,
  activeToken,
  isAuthenticated,
  extraParams,
  callbacks,
  displayName,
}: InitializeJitsiAPIParams): Promise<JitsiDisposableApi> => {
  const {
    setIsMeetingVisible,
    setIsChatOpen,
    removeOverlay,
    startDeviceSync,
    cleanup,
    setupPasswordListener,
    onJitsiError,
    clearJitsiErrors,
    onConferenceJoined,
    onLobbyEntered,
    onMeetingLeaving,
    navigate,
    removeSessionForMeetingLink,
    isMeetingSetUp,
    apiRef,
    cancelPendingRef,
  } = callbacks;

  if (isDev) {
    devLog('Initializing Jitsi API for meeting:', meetingId);
  }

  const tokenData = decodeJWT(activeToken);
  if (isDev) {
    devLog('[Dev] Decoded token data:', tokenData);
  }

  const jwtDisplayName =
    typeof tokenData?.context?.user?.name === 'string' ? tokenData.context.user.name.trim() : '';
  const resolvedDisplayName = jwtDisplayName || displayName?.trim() || undefined;

  // Ensure the meeting container stays hidden until the conference is ready
  const meetingContainer = document.querySelector('#meeting-container') as HTMLElement;
  if (meetingContainer) {
    meetingContainer.classList.remove('visible');
    meetingContainer.style.display = 'none';
  }

  // No iframe owns jitsiLocalStorage yet, and a previous teardown may have been
  // skipped, so clear the guard before the pre-join device writes below.
  setJitsiSessionActive(false);

  // Validate persisted device selections before building the join config.
  // External/USB device IDs aren't guaranteed stable across unplug/replug or
  // reboot the way built-in device IDs are (SM-1151), so a stored deviceId can
  // silently go stale. Also keep jitsiLocalStorage in sync with sangoma settings
  // so Jitsi doesn't resurrect a stale built-in mic id from a prior session.
  try {
    const { corrections } = await prepareDevicesForMeetingJoin();
    if (Object.keys(corrections).length > 0) {
      devLog('[DeviceReconciliation] Corrected stale device id(s) before join:', corrections);
      const currentSettings = settingsService.getSettings();
      await settingsService.updateSettings(
        { devices: { ...currentSettings.devices, ...corrections } },
        { validate: true, persist: true },
      );
    }
  } catch (e) {
    if (isDev) {
      console.error('[DeviceReconciliation] Failed to reconcile stored device ids:', e);
    }
  }

  // Build config overwrite
  const configOverwrite = buildJitsiConfigOverwrite({
    isAuthenticated,
    meetingId,
    meetingDisplayName,
    extraParams,
  });

  devLog('configOverwrite', configOverwrite);

  // Wait for Jitsi API to be available before initializing
  const JitsiMeetExternalApi = await waitForJitsiAPI();

  // Initialize Jitsi API
  const appId = getCurrentAppId();
  const api = new JitsiMeetExternalApi(APP_DOMAIN, {
    roomName: `${appId}/${meetingId}`,
    parentNode: meetingContainer,
    release: JAAS_RELEASE,
    jwt: activeToken,
    configOverwrite,
    ...(resolvedDisplayName ? { userInfo: { displayName: resolvedDisplayName } } : {}),
  });

  // From here until teardown the iframe owns jitsiLocalStorage.
  setJitsiSessionActive(true);

  // Setup password listener
  setupPasswordListener(api);

  apiRef.current = api;

  // biome-ignore lint/suspicious/noExplicitAny: dev helper for console debugging
  (window as any).jitsiApi = api;

  // Start a span for the lifetime of this meeting session so that JaaS log
  // events (which arrive via postMessage with no inherited Zone context) can
  // be emitted under a real trace/span id.
  const meetingSpan = getUiTracer().startSpan('jitsi-meeting-session', {
    attributes: { 'meeting.id': meetingId },
  });
  const meetingContext = trace.setSpan(context.active(), meetingSpan);
  const jaasLogger = getUiLogger('jaas');

  // Which saved device slots we have already pushed into Jitsi (SM-1151). Jitsi
  // finalizes its own device selection some time after join, so we re-attempt on
  // every `deviceListChanged` — but only for slots not yet satisfied, so a device
  // the user picks mid-meeting is never overridden.
  const appliedDeviceSlots = new Set<DeviceApplyKind>();
  // Serialized because the set is only written once an apply resolves, while
  // `videoConferenceJoined` and `deviceListChanged` fire within moments of each
  // other during startup. Run concurrently, both would read the same unsatisfied
  // slots and each push its own device switch into a conference that is still
  // coming up. Chaining makes the once-per-slot guarantee hold.
  let pendingDeviceApply: Promise<void> = Promise.resolve();
  const applySavedDevices = () => {
    pendingDeviceApply = pendingDeviceApply
      .then(async () => {
        const applied = await applyPersistedDevicesToJitsiApi(api, undefined, appliedDeviceSlots);
        for (const slot of applied) {
          appliedDeviceSlots.add(slot);
        }
      })
      .catch((e) => {
        if (isDev) {
          console.error('[DeviceApply] Failed to apply persisted devices:', e);
        }
      });
  };

  // Apply as soon as the API exists so prejoin can pick up the saved devices
  // (deviceListChanged / videoConferenceJoined retry if the list is still empty).
  applySavedDevices();

  // Capture device changes from the moment the API exists rather than on join.
  // With the prejoin screen enabled the user usually picks their mic/camera
  // *before* `videoConferenceJoined` fires, and starting there missed every one
  // of those selections (SM-1187 reverse sync). The API's transport is live as
  // soon as the constructor returns, so this is also the earliest we can
  // subscribe to Jitsi's storage write-back.
  //
  // Gate that write-back on appliedDeviceSlots: Jitsi's first default pick
  // (host deviceIds do not match inside the iframe) must not overwrite the
  // saved selection we are still trying to apply.
  startDeviceSync(api, {
    getAppliedSlots: () => appliedDeviceSlots,
    getSavedDevices: getPersistedDeviceSelections,
  });

  // Set meeting visible after a delay to allow Jitsi iframe to load
  devLog('[Jitsi] API created, waiting before setting isMeetingVisible...');
  const meetingVisibleDelayMs = 1500; // 1.5 second delay to let Jitsi iframe render
  const meetingVisibleTimeoutId = setTimeout(() => {
    devLog('[Jitsi] Setting isMeetingVisible to true');
    setIsMeetingVisible(true);
  }, meetingVisibleDelayMs);

  const teardownMeeting = (): MeetingExitAction => {
    setJitsiSessionActive(false);
    window.removeEventListener('offline', handleOffline);
    window.removeEventListener('online', handleOnline);
    exitMeetingFullscreen();

    const meetingContainer = document.querySelector('#meeting-container') as HTMLElement;
    if (meetingContainer) {
      meetingContainer.classList.remove('visible');
      meetingContainer.style.display = 'none';
    }
    document.body.classList.remove('meeting-active');
    setIsMeetingVisible(false);
    isMeetingSetUp.current = false;

    // biome-ignore lint/suspicious/noExplicitAny: dev helper cleanup
    if ((window as any).jitsiApi === api) {
      // biome-ignore lint/suspicious/noExplicitAny: dev helper cleanup
      delete (window as any).jitsiApi;
    }
    // biome-ignore lint/suspicious/noExplicitAny: Jitsi external API dev helper is untyped
    if (isDev && (window as any).testJitsi?.api === api) {
      // biome-ignore lint/suspicious/noExplicitAny: Jitsi external API dev helper is untyped
      delete (window as any).testJitsi;
    }

    cleanup();

    meetingSpan.end();

    try {
      api.dispose();
    } catch (e) {
      console.warn('[Cleanup] Error disposing Jitsi API in readyToClose:', e);
    }
    apiRef.current = null;

    const exitAction = meetingSessionService.setMeetingActive(false);
    removeSessionForMeetingLink();
    return exitAction;
  };

  // Always tear down state on readyToClose; only navigate when online.
  api.addEventListener('readyToClose', () => {
    onMeetingLeaving?.();
    const exitAction = teardownMeeting();

    if (!navigator.onLine) {
      console.warn('[Jitsi] readyToClose fired while offline — skipping navigation.');
      return;
    }

    // A deferred OAuth login (session expired) already started a full-page redirect;
    // navigating here would fight it. Skip saving this (now-ended) meeting URL as the
    // post-login return target.
    if (exitAction === 'skip') {
      authRedirectService.skipNextPreLoginUrl();
      return;
    }

    // Cross-tab auth changed: a full page load is needed to pick up the new auth state, and
    // targeting '/' avoids reloading the now-stale meeting URL.
    if (exitAction === 'reload') {
      window.location.href = '/';
      return;
    }

    if (!closeMeetingPopup()) {
      // Hosted-mode meetings run in a popup window — close it on meeting end
      // instead of navigating the popup to the home page.
      navigate('/');
    }
  });

  // Gate error reporting until the user has actually joined the conference.
  // Pre-join errors (lobby/config) should not show the banner.
  let hasJoinedConference = false;

  // When the user leaves (or gets disconnected from) the conference,
  // clear stale error banners and stop gating new errors.
  api.addEventListener('videoConferenceLeft', () => {
    devLog('[Jitsi] videoConferenceLeft event');
    hasJoinedConference = false;
    onConferenceJoined?.();
    clearJitsiErrors();
  });

  // SM-786: Detect lobby rejection via Jitsi notifications.
  // biome-ignore lint/suspicious/noExplicitAny: Jitsi event payloads have no TypeScript definitions
  api.addEventListener('notificationTriggered', (event: any) => {
    const title = (event?.title ?? '').toLowerCase();
    if (title.includes('rejected') || title.includes('denied')) {
      devLog('[Jitsi] Lobby rejection notification — hiding lobby banner');
      onConferenceJoined?.();
    }
  });

  // When the conference is actually joined (or re-joined after reconnect),
  // reveal the container and clear any error banners.
  api.addEventListener('videoConferenceJoined', () => {
    hasJoinedConference = true;
    clearJitsiErrors();

    // SpanProcessor.onStart applied an empty cache when the meeting span was created
    // — jitsi only writes callStatsUserName during conference join.
    refreshCallStatsUserName();
    applyClientMetadata(meetingSpan);

    if (configOverwrite?.tileView) {
      api.executeCommand('toggleTileView');
    }

    devLog('[Jitsi] videoConferenceJoined event - User joined conference');
    const mc = document.querySelector('#meeting-container') as HTMLElement;
    if (mc) {
      mc.style.display = 'block';
      void mc.offsetHeight;
      mc.classList.add('visible');
    }
    document.body.classList.add('meeting-active');
    setIsMeetingVisible(true);
    onConferenceJoined?.();

    // First attempt at the saved selection; `deviceListChanged` covers the case
    // where Jitsi has not finished enumerating devices yet (SM-1151).
    applySavedDevices();
  });

  // Jitsi's device list is populated (and re-populated on hardware changes)
  // asynchronously. This is the deterministic signal that a saved device we
  // could not resolve before may now be selectable, replacing the timing
  // guesswork a delay-based retry would need (SM-1151).
  api.addEventListener('deviceListChanged', () => {
    applySavedDevices();
  });

  // biome-ignore lint/suspicious/noExplicitAny: Jitsi event payloads have no TypeScript definitions
  api.addEventListener('log', (payload: any) => {
    let bodyMessage: string = '';
    let jaasTimestamp: string | null = null;

    const severityText = String(payload?.logLevel ?? 'info').toUpperCase();

    if (Array.isArray(payload?.args)) {
      const levelArg: string = `[${severityText}]`;

      let args: unknown[] = [];

      // check jaas format, if it matches drop redundant data
      if (payload.args.length > 2 && payload.args[1] === levelArg) {
        args = payload.args.slice(2);
        jaasTimestamp = payload.args[0];
      } else {
        args = payload.args;
      }

      bodyMessage = args
        .map((a) => (typeof a === 'object' ? JSON.stringify(a) : String(a)))
        .join(' ');
    } else {
      bodyMessage = String(payload.args);
    }

    const attributes: {
      'body.message': string;
      'meeting.id': string;
      'body.jaas_timestamp'?: string;
    } = {
      'body.message': bodyMessage,
      'meeting.id': meetingId,
    };

    if (jaasTimestamp) {
      attributes['body.jaas_timestamp'] = jaasTimestamp;
    }

    jaasLogger.emit({
      severityText,
      context: meetingContext,
      attributes,
    });
  });

  // Device-related debug events
  // biome-ignore lint/suspicious/noExplicitAny: Jitsi event payloads have no TypeScript definitions
  api.addEventListener('deviceListChanged', (payload: any) => {
    if (isDev) {
      devLog('[Jitsi] handleDeviceListChanged:', payload);
    }
  });

  // biome-ignore lint/suspicious/noExplicitAny: Jitsi event payloads have no TypeScript definitions
  api.addEventListener('audioAvailabilityChanged', (payload: any) => {
    if (isDev) {
      devLog('[Jitsi] audioAvailabilityChanged:', payload);
    }
  });

  // biome-ignore lint/suspicious/noExplicitAny: Jitsi event payloads have no TypeScript definitions
  api.addEventListener('videoAvailabilityChanged', (payload: any) => {
    if (isDev) {
      devLog('[Jitsi] videoAvailabilityChanged:', payload);
    }
  });

  // Listen for chat updates to manage chat state and transcript panel
  // biome-ignore lint/suspicious/noExplicitAny: Jitsi event payloads have no TypeScript definitions
  api.addEventListener('chatUpdated', (payload: any) => {
    if (isDev) {
      devLog('[Jitsi] chatUpdated:', payload);
    }

    // Update chat state
    const chatIsOpen = payload && payload.isOpen === true;
    setIsChatOpen(chatIsOpen);

    // Dispatch custom event for components to listen to
    window.dispatchEvent(
      new CustomEvent('jitsi-chat-updated', {
        detail: { isOpen: chatIsOpen },
      }),
    );

    // If chat panel is opened, close the transcript panel
    if (chatIsOpen) {
      try {
        removeOverlay('transcript-panel');
      } catch (error) {
        console.error('Error closing transcript panel:', error);
      }
    }
  });

  // SM-1326: fullscreen must be requested by the host page so the browser's exit
  // prompt names our domain instead of 8x8.vc.
  // biome-ignore lint/suspicious/noExplicitAny: Jitsi event payloads have no TypeScript definitions
  api.addEventListener('toolbarButtonClicked', (payload: any) => {
    if (payload?.key === FULLSCREEN_BUTTON_ID) {
      void toggleMeetingFullscreen();
    }
  });

  // Listen for errors from the Jitsi iframe (SM-1037).
  // Only surface errors that happen after the user has joined the conference.
  // The external API wraps the data in an `error` property.
  // biome-ignore lint/suspicious/noExplicitAny: Jitsi event payloads have no TypeScript definitions
  api.addEventListener('errorOccurred', (event: any) => {
    const payload: JitsiErrorPayload = event?.error ?? event ?? {};
    console.error('[Jitsi] errorOccurred:', payload);

    // SM-786: Detect lobby/knocking phase and rejection.
    const name = (payload.name ?? '').toLowerCase();
    if (name.includes('membersonly') || name.includes('members_only')) {
      devLog('[Jitsi] Lobby detected (membersOnly) — showing lobby banner');
      onLobbyEntered?.();
    }
    if (name.includes('rejected') || name.includes('not-allowed') || name.includes('not_allowed')) {
      devLog('[Jitsi] Lobby rejection detected — hiding lobby banner');
      onConferenceJoined?.();
    }

    if (!(payload.name && hasJoinedConference)) {
      devDebug('[Jitsi] Ignoring error — missing name or conference not yet joined:', {
        hasName: !!payload.name,
        hasJoinedConference,
      });
      return;
    }
    onJitsiError(payload);
  });

  // Catch mid-call connectivity drops that Jitsi doesn't report via errorOccurred.
  // biome-ignore lint/suspicious/noExplicitAny: Jitsi event payloads have no TypeScript definitions
  api.addEventListener('peerConnectionFailure', (payload: any) => {
    console.error('[Jitsi] peerConnectionFailure:', payload);
    if (hasJoinedConference) {
      const wasConnected = payload?.wasConnected ?? false;
      onJitsiError({
        name: 'peerConnectionFailure',
        type: 'CONNECTION',
        message: wasConnected
          ? 'Meeting bridge connection was disrupted - please check your connection'
          : 'Could not establish connection to the meeting bridge - please check your connection',
        isFatal: false,
      });
    }
  });

  // When the browser goes offline, hide the Jitsi iframe so the user doesn't
  // see Chrome's "No internet" dino page inside it (Jitsi tries to reload the
  // iframe internally). Show an error banner instead. On reconnect, dispose the
  // dead API and let the useJitsiMeeting effect re-initialise a fresh session.
  const handleOffline = () => {
    console.warn('[Jitsi] Browser went offline — showing banner.');
    onJitsiError({
      name: 'networkOffline',
      type: 'CONNECTION',
      message: 'You are offline. Waiting for connection to resume…',
      isFatal: true,
    });
  };

  const handleOnline = () => {
    console.info('[Jitsi] Browser back online — will reload shortly.');
    window.removeEventListener('offline', handleOffline);
    window.removeEventListener('online', handleOnline);
    clearJitsiErrors();
    onJitsiError({
      name: 'networkReconnecting',
      type: 'CONNECTION',
      message: 'Connection restored, resuming meeting…',
      isFatal: false,
    });
    const networkStabilizeDelayMs = 3000;
    setTimeout(() => clearJitsiErrors(), networkStabilizeDelayMs);
  };

  window.addEventListener('offline', handleOffline);
  window.addEventListener('online', handleOnline);

  // Fallback: if for some reason joined event doesn't arrive in time, reveal after 3s
  const meetingFallbackRevealDelayMs = 3000;
  const fallbackRevealTimeoutId = setTimeout(() => {
    const mc = document.querySelector('#meeting-container') as HTMLElement;
    if (mc && mc.style.display !== 'block') {
      mc.style.display = 'block';
      void mc.offsetHeight;
      mc.classList.add('visible');
    }
  }, meetingFallbackRevealDelayMs);

  cancelPendingRef.current = () => {
    clearTimeout(meetingVisibleTimeoutId);
    clearTimeout(fallbackRevealTimeoutId);
    window.removeEventListener('offline', handleOffline);
    window.removeEventListener('online', handleOnline);
    exitMeetingFullscreen();
    cancelPendingRef.current = null;
  };

  return api;
};
